const SWAP_CACHE = window.__ctrProductToggleCache || (window.__ctrProductToggleCache = new Map())

class ProductToggle extends HTMLElement {
  #controller = null
  #activeKey = ''
  #requestId = 0

  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('[data-product-toggle-option]'))
    if (this.buttons.length < 2) return

    this.#activeKey =
      window.__ctrProductToggleActiveKey ||
      this.#keyFromLocation() ||
      this.#defaultKey() ||
      this.#optionKey(this.buttons[0])
    window.__ctrProductToggleActiveKey = this.#activeKey
    this.#updateSelected()
    this.addEventListener('click', this.#onClick)
    this.addEventListener('keydown', this.#onKeydown)
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick)
    this.removeEventListener('keydown', this.#onKeydown)
  }

  #onClick = (event) => {
    const button = event.target.closest('[data-product-toggle-option]')
    if (!button || !this.contains(button)) return
    event.preventDefault()
    this.#selectButton(button)
  }

  #onKeydown = (event) => {
    const button = event.target.closest('[data-product-toggle-option]')
    if (!button || !this.contains(button)) return

    const currentIndex = this.buttons.indexOf(button)
    let nextIndex = -1

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % this.buttons.length
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + this.buttons.length) % this.buttons.length
    } else if (event.key === 'Home') {
      nextIndex = 0
    } else if (event.key === 'End') {
      nextIndex = this.buttons.length - 1
    }

    if (nextIndex < 0) return
    event.preventDefault()
    this.buttons[nextIndex].focus()
    this.#selectButton(this.buttons[nextIndex])
  }

  async #selectButton(button) {
    const key = this.#optionKey(button)
    if (!key || key === this.#activeKey) return

    const nextUrl = this.#mergedUrl(button.dataset.url)
    const action = button.dataset.clickAction || 'swap'

    if (action === 'navigate') {
      window.location.assign(nextUrl.toString())
      return
    }

    this.#controller?.abort()
    this.#controller = new AbortController()
    this.#requestId += 1
    const requestId = this.#requestId

    const overlayRoot = action === 'swap_main' ? document.querySelector('main') : this.#swapRoot()
    if (!overlayRoot) return

    const previousKey = this.#activeKey
    const scrollY = window.scrollY
    let swapped = false
    window.__ctrProductToggleActiveKey = key
    this.#setBusy(true, overlayRoot)
    this.#showOverlay(overlayRoot)

    try {
      const markup = await this.#fetchPageMarkup(nextUrl.toString(), this.#controller.signal)
      if (requestId !== this.#requestId) return

      if (action === 'swap_main') {
        await this.#replaceMain(markup)
      } else {
        await this.#replaceSwapRoot(markup)
      }

      this.#syncUrl(nextUrl)
      swapped = true
    } catch (error) {
      if (error.name === 'AbortError' || requestId !== this.#requestId) return
      console.error('Product toggle: failed to swap content', error)
      window.__ctrProductToggleActiveKey = previousKey
      const liveRoot = action === 'swap_main' ? document.querySelector('main') : this.#swapRoot()
      this.#hideOverlay(liveRoot)
      this.#setBusy(false, liveRoot)
    } finally {
      if (requestId !== this.#requestId) return
      this.#restoreScroll(scrollY, swapped)
    }
  }

  #swapRoot() {
    return this.closest('.js-product-toggle-swap') || document.querySelector('.js-product-toggle-swap')
  }

  #restoreScroll(scrollY, swapped) {
    if (swapped && this.#isMobile()) {
      this.#alignToToggle()
      return
    }

    window.scrollTo(0, scrollY)
  }

  #isMobile() {
    return window.matchMedia('(max-width: 767px)').matches
  }

  #alignToToggle() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const behavior = reduceMotion ? 'auto' : 'smooth'
    const hasStats = Boolean(document.querySelector('[data-transformation-stats]'))

    const run = () => {
      const toggle = document.querySelector('product-toggle')
      if (!toggle) return

      const offset = this.#stickyOffset() + this.#toggleAlignGap()
      const top = Math.max(0, toggle.getBoundingClientRect().top + window.scrollY - offset)
      if (Math.abs(window.scrollY - top) < 2) return

      window.scrollTo({ top, behavior })
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.setTimeout(run, hasStats ? 350 : 0)
      })
    })
  }

  #toggleAlignGap() {
    const slot = document.querySelector('[data-product-toggle-slot]:not(:empty)')
    if (!slot) return 16
    return parseFloat(getComputedStyle(slot).paddingTop) || 16
  }

  #stickyOffset() {
    if (typeof window.magna?.getStickyScrollOffset === 'function') {
      return window.magna.getStickyScrollOffset()
    }

    const announcementHeight =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--announcement-total-height')) || 0
    const header =
      document.querySelector('.ctr-header-section') ||
      document.querySelector('.section-header') ||
      document.querySelector('.header')

    return announcementHeight + (header?.offsetHeight || 0)
  }

  #optionKey(button) {
    return this.#normalizeUrl(button?.dataset?.url || '')
  }

  #defaultKey() {
    const button = this.buttons.find((item) => item.dataset.selectedByDefault === 'true')
    return button ? this.#optionKey(button) : ''
  }

  #keyFromLocation() {
    const current = this.#normalizeUrl(window.location.href)
    const match = this.buttons.find((button) => this.#optionKey(button) === current)
    return match ? this.#optionKey(match) : ''
  }

  #normalizeUrl(urlString) {
    if (!urlString) return ''
    try {
      const url = new URL(urlString, window.location.origin)
      const pathname = url.pathname.replace(/\/+$/, '') || '/'
      const view = url.searchParams.get('view')
      return view ? `${pathname}?view=${view}` : pathname
    } catch (error) {
      return urlString
    }
  }

  #mergedUrl(optionUrl) {
    const next = new URL(optionUrl || '/', window.location.origin)
    const current = new URL(window.location.href)

    current.searchParams.forEach((value, key) => {
      if (!next.searchParams.has(key)) next.searchParams.set(key, value)
    })

    if (current.hash && !next.hash) next.hash = current.hash
    return next
  }

  #syncUrl(nextUrl) {
    const next = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (next === current) return
    window.history.replaceState({ ...window.history.state }, '', next)
  }

  #updateSelected() {
    this.buttons.forEach((button) => {
      const selected = this.#optionKey(button) === this.#activeKey
      button.classList.toggle('is-selected', selected)
      button.setAttribute('aria-checked', selected ? 'true' : 'false')
      button.tabIndex = selected ? 0 : -1
    })
  }

  #setBusy(isBusy, overlayRoot) {
    this.dataset.busy = isBusy ? 'true' : 'false'
    this.buttons.forEach((button) => {
      button.setAttribute('aria-disabled', isBusy ? 'true' : 'false')
    })
    if (!overlayRoot) return
    overlayRoot.classList.toggle('is-swapping', isBusy)
    overlayRoot.setAttribute('aria-busy', isBusy ? 'true' : 'false')
  }

  #showOverlay(overlayRoot) {
    if (!overlayRoot) return
    let overlay = overlayRoot.querySelector(':scope > .ctr-product-toggle-overlay')
    if (!overlay) {
      overlay = document.createElement('div')
      overlay.className = 'ctr-product-toggle-overlay'
      overlay.setAttribute('aria-hidden', 'true')
      overlay.innerHTML = '<span class="ctr-product-toggle-overlay__spinner"></span>'
      overlayRoot.appendChild(overlay)
    }
  }

  #hideOverlay(overlayRoot) {
    overlayRoot?.querySelector(':scope > .ctr-product-toggle-overlay')?.remove()
  }

  async #fetchPageMarkup(optionUrl, signal) {
    const cacheKey = this.#normalizeUrl(optionUrl)
    if (SWAP_CACHE.has(cacheKey)) return SWAP_CACHE.get(cacheKey)

    const response = await fetch(optionUrl, {
      signal,
      credentials: 'same-origin',
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)

    const markup = await response.text()
    SWAP_CACHE.set(cacheKey, markup)
    return markup
  }

  #parsePage(markup) {
    const staging = document.createElement('template')
    staging.innerHTML = markup
    const titleNode = staging.content.querySelector('title')
    const titleMatch = markup.match(/<title[^>]*>([\s\S]*?)<\/title>/i)

    return {
      swap: staging.content.querySelector('.js-product-toggle-swap'),
      main: staging.content.querySelector('main'),
      title: (titleNode?.textContent || titleMatch?.[1] || '').replace(/<[^>]+>/g, '').trim(),
    }
  }

  async #replaceSwapRoot(markup) {
    const current = this.#swapRoot()
    const { swap: next } = this.#parsePage(markup)
    if (!current || !next) throw new Error('No buy box markup returned')
    await this.#swapNode(current, next)
  }

  async #replaceMain(markup) {
    const current = document.querySelector('main')
    const { main: next, title } = this.#parsePage(markup)
    if (!current || !next) throw new Error('No main markup returned')
    await this.#swapNode(current, next)
    if (title) document.title = title
  }

  async #swapNode(current, next) {
    if (window.Alpine?.destroyTree) {
      window.Alpine.destroyTree(current)
    }

    this.#emitSectionEvents(current, 'shopify:section:unload')
    await this.#applySwapStyles(next)
    this.#rememberLoadedScripts()
    current.replaceWith(next)
    await this.#executeScripts(next)
    this.#reinit(next)
    this.#reinitStats(next)
    this.#emitSectionEvents(next, 'shopify:section:load')
  }

  #applySwapStyles(root) {
    document.querySelectorAll('link[data-product-toggle-css]').forEach((link) => link.remove())

    const pending = []
    root.querySelectorAll('link[rel="stylesheet"], link[rel="preload"][as="style"]').forEach((link) => {
      const href = link.getAttribute('href')
      if (!href) return

      const next = document.createElement('link')
      next.rel = 'stylesheet'
      next.href = href
      next.setAttribute('data-product-toggle-css', '')
      pending.push(
        new Promise((resolve) => {
          next.addEventListener('load', resolve, { once: true })
          next.addEventListener('error', resolve, { once: true })
          if (next.sheet) resolve()
        }),
      )
      document.head.appendChild(next)
      link.remove()
    })

    return Promise.all(pending)
  }

  #loadedScriptSrcs() {
    if (!window.__ctrProductToggleLoadedScripts) {
      window.__ctrProductToggleLoadedScripts = new Set()
    }
    return window.__ctrProductToggleLoadedScripts
  }

  #scriptKey(script) {
    const src = script?.src || script?.getAttribute?.('src')
    if (!src) return ''
    try {
      return new URL(src, window.location.origin).pathname
    } catch (error) {
      return src
    }
  }

  #rememberLoadedScripts() {
    const loaded = this.#loadedScriptSrcs()
    Array.from(document.scripts).forEach((script) => {
      const key = this.#scriptKey(script)
      if (key) loaded.add(key)
    })
  }

  #isExecutableScript(node) {
    const type = (node.getAttribute('type') || 'text/javascript').trim().toLowerCase()
    return (
      type === '' ||
      type === 'text/javascript' ||
      type === 'application/javascript' ||
      type === 'text/ecmascript' ||
      type === 'module'
    )
  }

  #scriptAlreadyLoaded(node) {
    const key = this.#scriptKey(node)
    if (!key) return false
    return this.#loadedScriptSrcs().has(key)
  }

  async #executeScripts(root) {
    const pending = []
    const loaded = this.#loadedScriptSrcs()

    Array.from(root.querySelectorAll('script')).forEach((oldScript) => {
      if (!this.#isExecutableScript(oldScript)) return

      const src = oldScript.getAttribute('src')
      if (src && this.#scriptAlreadyLoaded(oldScript)) {
        oldScript.remove()
        return
      }

      const next = document.createElement('script')
      Array.from(oldScript.attributes).forEach(({ name, value }) => {
        next.setAttribute(name, value)
      })
      if (!src) next.textContent = oldScript.textContent

      if (src) {
        loaded.add(this.#scriptKey(next) || this.#scriptKey(oldScript))
        pending.push(
          new Promise((resolve) => {
            next.addEventListener('load', resolve, { once: true })
            next.addEventListener('error', resolve, { once: true })
          }),
        )
      }

      oldScript.replaceWith(next)
    })

    await Promise.all(pending)
  }

  #emitSectionEvents(root, eventName) {
    const sections = []
    if (root.matches?.('.shopify-section')) sections.push(root)
    sections.push(...root.querySelectorAll('.shopify-section'))

    sections.forEach((section) => {
      section.dispatchEvent(
        new CustomEvent(eventName, {
          bubbles: true,
          detail: { sectionId: section.id.replace(/^shopify-section-/, '') },
        }),
      )
    })
  }

  #reinit(root) {
    if (window.Alpine?.initTree) {
      window.Alpine.initTree(root)
    }

    this.#reinitKlaviyo(root)
  }

  #reinitStats(root) {
    if (!root.querySelector('[data-transformation-stats]')) return

    const run = () => {
      if (typeof window.initTransformationStats === 'function') {
        window.initTransformationStats(root)
      }
    }

    run()
    requestAnimationFrame(() => {
      requestAnimationFrame(run)
    })
  }

  #reinitKlaviyo(root) {
    if (!root.querySelector('.klaviyo-star-rating-widget')) return
    if (typeof window.klaviyoReviewsInitializeWidgets === 'function') {
      window.klaviyoReviewsInitializeWidgets()
    }
  }
}

if (!customElements.get('product-toggle')) {
  customElements.define('product-toggle', ProductToggle)
}
